import { arrayMove } from "@dnd-kit/sortable";

const COMPLETED_TASK_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export const isTaskEmpty = (task) =>
  !task.description?.trim() &&
  !task.priority &&
  !task.outcome?.trim() &&
  !task.client_id;

export const todayIsoDate = () => new Date().toISOString().slice(0, 10);

export const addDaysIso = (isoDate, days) => {
  const d = new Date(`${isoDate}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const daysDiff = (isoDate, referenceIso) => {
  const a = new Date(`${isoDate}T00:00:00`);
  const b = new Date(`${referenceIso}T00:00:00`);
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
};

export const getScheduleStatus = (scheduledDate, selectedDate) => {
  if (!scheduledDate) return { type: "today", label: "Today" };
  const diff = daysDiff(scheduledDate, selectedDate);
  if (diff < 0)
    return {
      type: "overdue",
      label: diff === -1 ? "1d overdue" : `${Math.abs(diff)}d overdue`,
    };
  if (diff === 0) return { type: "today", label: "Today" };
  if (diff === 1) return { type: "tomorrow", label: "Tomorrow" };
  return { type: "future", label: formatShortDate(scheduledDate) };
};

export const formatShortDate = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  });

/** Hide completed tasks only after 24 hours */
export const purgeExpiredCompletedTasks = (tasks) => {
  const now = Date.now();
  return tasks.filter((task) => {
    if (!task.completed) return true;
    if (!task.completed_at) return true;
    return now - new Date(task.completed_at).getTime() < COMPLETED_TASK_TTL_MS;
  });
};

/**
 * Show ALL tasks for this log date.
 * Only completed tasks older than 24h are removed (via purge).
 * Order: overdue → pending → completed (still within 24h)
 */
export const orderDailyTasks = (tasks) => {
  const visible = purgeExpiredCompletedTasks(tasks);

  return [...visible].sort((a, b) => {
    // Completed tasks always after active ones
    if (a.completed !== b.completed) {
      return a.completed ? 1 : -1;
    }
    // Respect backend sort_order
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });
};

export const reorderPendingTasks = (tasks, activeId, overId) => {
  const pending = tasks.filter((t) => !t.completed);
  const completed = tasks.filter((t) => t.completed);

  const oldIndex = pending.findIndex((t) => t.id === activeId);
  const newIndex = pending.findIndex((t) => t.id === overId);

  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
    return tasks;
  }

  const reordered = arrayMove(pending, oldIndex, newIndex).map((task, index) => ({
    ...task,
    sort_order: index + 1,
  }));

  return [...reordered, ...completed];
};

export const validateDailyTask = (task) => {
  const errors = [];
  if (!task.description?.trim()) errors.push("description");
  if (!task.priority) errors.push("priority");
  return errors;
};

export const validateAllDailyTasks = (tasks) =>
  tasks.reduce((acc, task) => {
    const errors = validateDailyTask(task);
    if (errors.length) acc.push({ id: task.id, errors });
    return acc;
  }, []);

export const computeDailyLogSummary = (tasks) => {
  const total = tasks.length;
  const completed = tasks.filter((t) => t.completed).length;
  return {
    total,
    completed,
    pending: total - completed,
    completionPct: total === 0 ? 0 : Math.round((completed / total) * 100),
  };
};

const ACTIVE_STATUSES = ["In Progress", "On Hold", "Review", "Blocked"];

export const isReportCompleted = (task) =>
  Boolean(task.completed) ||
  (task.progress_percent ?? 0) >= 100 ||
  task.status === "Completed";

export const isReportInProgress = (task) => {
  if (isReportCompleted(task)) return false;
  const progress = task.progress_percent ?? 0;
  if (progress > 0 && progress < 100) return true;
  return ACTIVE_STATUSES.includes(task.status);
};

export const isReportNotStarted = (task) =>
  !isReportCompleted(task) && !isReportInProgress(task);

export const countReportBuckets = (tasks) => ({
  completed: tasks.filter(isReportCompleted).length,
  inProgress: tasks.filter(isReportInProgress).length,
  notStarted: tasks.filter(isReportNotStarted).length,
});

/** Tasks included in today's report — excludes future-scheduled work */
export const filterTodayReportTasks = (tasks, selectedDate) =>
  tasks.filter((task) => {
    if (!task?.scheduled_date) return true;
    return task.scheduled_date <= selectedDate;
  });

export const formatLogDate = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });